/**
 * useAudioEnergy - vocalisation detection from the raw microphone signal
 *
 * The companion to useSpeechRecognition, and deliberately NOT part of it.
 *
 * WHY IT HAS TO EXIST
 * The Web Speech API returns words or nothing. A child who says "buh" produces
 * a `no-speech` error and no transcript at all - the recogniser has correctly
 * reported that no English word was spoken, and in doing so has thrown away
 * the only thing Phase 1 component B is asking about.
 *
 * Phase 1 B is "Attempts Vocalization: any sound in response to prompt". It is
 * 5 of the 208 boxes, and it is the box that makes Phase 1 mean anything for a
 * pre-verbal child - the children the framework exists for. It cannot be
 * measured by speech recognition, at all, in any browser. It needs the audio
 * signal: getUserMedia, an AnalyserNode, an RMS envelope, and a noise floor.
 *
 * See docs/audit/COMPONENT-JUDGEMENT.md section 7.2.
 *
 * ---------------------------------------------------------------------------
 * DIVISION OF LABOUR
 *
 *   useAudioEnergy         Web Audio plumbing and React lifecycle (this file)
 *   vocalisationDetector   the detection rules - pure, no DOM, unit tested
 *   engine/trial.js        the record shape, including the audio block
 *   engine/judgement.js    judgePhase1B - what counts as a success
 *
 * Nothing here decides whether a child passed anything. It produces the
 * `audio` block of a trial record and hands it to the engine.
 *
 * ---------------------------------------------------------------------------
 * getUserMedia CONSTRAINTS
 *
 * `noiseSuppression` and `autoGainControl` are requested OFF, and this is not
 * an optimisation. Noise suppression is built to remove exactly the
 * non-speech-shaped sounds this hook exists to detect, and automatic gain
 * control rescales the signal continuously, which makes an RMS envelope
 * uncomparable with the noise floor measured a second earlier. Echo
 * cancellation stays ON, because Pip speaking through the device's own
 * speakers would otherwise be detected as the child vocalising.
 *
 * Browsers may ignore all three. The detector's adaptive floor is what makes
 * it still work when they do.
 *
 * ---------------------------------------------------------------------------
 * THE CLOCK
 *
 * Timestamps are performance.now(), which is what engine/trial.js requires.
 * A game must stamp its own trial events from the same clock; mixing in
 * Date.now() is rejected by validateTrialRecord().
 *
 * ---------------------------------------------------------------------------
 * MICROPHONE SHARING
 *
 * Running this alongside useSpeechRecognition opens the microphone twice - one
 * getUserMedia stream here, one inside the browser's speech service. Chrome
 * permits it and shows one indicator. Permission is requested once and both
 * are covered; if the user denies, this hook reports `permission: 'denied'`
 * and every trial records `monitored: false`, which the engine reads as "not
 * measured" rather than "did not vocalise".
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  createVocalisationDetector,
  rmsOf,
  rmsOfBytes,
} from './vocalisationDetector.js';

const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
};

/**
 * @param {object} [options]
 * @param {object} [options.detector]   overrides for DETECTOR_DEFAULTS
 * @param {number} [options.uiUpdateHz=10] how often the returned React state
 *        is refreshed. The analysis loop runs at animation-frame rate; setting
 *        React state 60 times a second would re-render the whole game around
 *        a number nobody can read that fast.
 * @param {number} [options.fftSize=2048]
 * @returns {object} see the return statement
 */
export function useAudioEnergy(options = {}) {
  const {
    detector: detectorOptions,
    uiUpdateHz = 10,
    fftSize = 2048,
  } = options;

  const [isSupported, setIsSupported] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [permission, setPermission] = useState('unknown'); // unknown|granted|denied
  const [error, setError] = useState(null);
  const [level, setLevel] = useState({ rms: 0, noiseFloor: null, isVocalising: false });
  const [vocalisationCount, setVocalisationCount] = useState(0);

  const detectorRef = useRef(null);
  const streamRef = useRef(null);
  const contextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);
  const bufferRef = useRef(null);
  const byteBufferRef = useRef(null);
  const lastUiAtRef = useRef(0);
  const trialStartRef = useRef(null);
  const onVocalisationRef = useRef(null);

  if (detectorRef.current === null) {
    detectorRef.current = createVocalisationDetector(detectorOptions);
  }

  useEffect(() => {
    const hasMedia = typeof navigator !== 'undefined'
      && navigator.mediaDevices
      && typeof navigator.mediaDevices.getUserMedia === 'function';
    const hasAudio = typeof window !== 'undefined'
      && Boolean(window.AudioContext || window.webkitAudioContext);
    setIsSupported(Boolean(hasMedia && hasAudio));
  }, []);

  /** Tear down everything, in the reverse order it was built. */
  const teardown = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (e) { /* already gone */ }
      sourceRef.current = null;
    }
    analyserRef.current = null;
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try { track.stop(); } catch (e) { /* already stopped */ }
      }
      streamRef.current = null;
    }
    if (contextRef.current) {
      const ctx = contextRef.current;
      contextRef.current = null;
      // close() is async and rejects if already closed; nothing waits on it.
      try { ctx.close(); } catch (e) { /* already closed */ }
    }
    setIsMonitoring(false);
    setLevel((prev) => ({ ...prev, rms: 0, isVocalising: false }));
  }, []);

  const tick = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const now = performance.now();

    let rms;
    if (typeof analyser.getFloatTimeDomainData === 'function') {
      analyser.getFloatTimeDomainData(bufferRef.current);
      rms = rmsOf(bufferRef.current);
    } else {
      // Older Safari has no float time-domain read.
      analyser.getByteTimeDomainData(byteBufferRef.current);
      rms = rmsOfBytes(byteBufferRef.current);
    }

    const result = detectorRef.current.pushFrame(rms, now);

    for (const event of result.emitted) {
      if (event.type === 'vocalisation') {
        setVocalisationCount((n) => n + 1);
        if (onVocalisationRef.current) onVocalisationRef.current(event);
      }
    }

    if (now - lastUiAtRef.current >= 1000 / uiUpdateHz) {
      lastUiAtRef.current = now;
      setLevel({
        rms: result.rms,
        noiseFloor: result.noiseFloor,
        isVocalising: result.isVocalising,
      });
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [uiUpdateHz]);

  /**
   * Open the microphone and start analysing.
   * @returns {Promise<boolean>} true when monitoring is running
   */
  const start = useCallback(async () => {
    if (!isSupported) {
      setError('This browser cannot monitor audio energy');
      return false;
    }
    if (contextRef.current) return true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      streamRef.current = stream;
      setPermission('granted');

      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      contextRef.current = ctx;
      // Autoplay policy suspends a context created before a user gesture.
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch (e) { /* stays suspended; rms reads 0 */ }
      }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = fftSize;
      // No smoothing: this reads the time domain, and smoothing would blur the
      // onset the whole hook exists to timestamp.
      analyser.smoothingTimeConstant = 0;
      analyserRef.current = analyser;

      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;
      // Deliberately NOT connected to ctx.destination - that would play the
      // child's microphone back through the speakers.

      bufferRef.current = new Float32Array(analyser.fftSize);
      byteBufferRef.current = new Uint8Array(analyser.fftSize);

      detectorRef.current.recalibrate();
      setError(null);
      setIsMonitoring(true);
      rafRef.current = requestAnimationFrame(tick);
      return true;
    } catch (err) {
      teardown();
      if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
        setPermission('denied');
        setError('Microphone permission denied');
      } else if (err && err.name === 'NotFoundError') {
        setError('No microphone found');
      } else {
        setError(err && err.message ? err.message : 'Could not start audio monitoring');
      }
      return false;
    }
  }, [isSupported, fftSize, tick, teardown]);

  const stop = useCallback(() => {
    detectorRef.current.flush(performance.now());
    teardown();
  }, [teardown]);

  /**
   * Open a trial window. Clears the event buffer so the audio block returned
   * by endTrial() belongs to this trial and nothing before it.
   * @param {number} [at] defaults to now
   * @returns {number} the trial start timestamp, for the trial record
   */
  const startTrial = useCallback((at) => {
    const t = Number.isFinite(at) ? at : performance.now();
    detectorRef.current.clear();
    trialStartRef.current = t;
    setVocalisationCount(0);
    return t;
  }, []);

  /**
   * Close the trial window and return its `audio` block, ready to drop into a
   * trial record.
   *
   * Any vocalisation still open is flushed first. A child who is still making
   * a sound when the trial timer expires has vocalised, and dropping that
   * event would fail Phase 1 B for the clearest possible success.
   *
   * @param {number} [at] defaults to now
   * @returns {{startedAt:number, endedAt:number, audio:object}}
   */
  const endTrial = useCallback((at) => {
    const t = Number.isFinite(at) ? at : performance.now();
    detectorRef.current.flush(t);
    const startedAt = trialStartRef.current ?? t;
    const audio = detectorRef.current.getAudioBlock({ from: startedAt, to: t });
    trialStartRef.current = null;
    return { startedAt, endedAt: t, audio };
  }, []);

  /**
   * The audio block for a window, without closing the trial. For a game that
   * wants to check mid-trial whether anything has been heard yet.
   */
  const getAudioBlock = useCallback((range) => detectorRef.current.getAudioBlock(range), []);

  /** Register a callback fired on each completed vocalisation. */
  const onVocalisation = useCallback((callback) => {
    onVocalisationRef.current = callback;
  }, []);

  /** Re-learn the room, e.g. after moving to a noisier one. */
  const recalibrate = useCallback(() => detectorRef.current.recalibrate(), []);

  useEffect(() => teardown, [teardown]);

  return {
    isSupported,
    isMonitoring,
    permission,
    error,

    rms: level.rms,
    noiseFloor: level.noiseFloor,
    isVocalising: level.isVocalising,
    vocalisationCount,

    start,
    stop,
    startTrial,
    endTrial,
    getAudioBlock,
    onVocalisation,
    recalibrate,

    /** Escape hatch for tests and diagnostics. */
    getDetectorState: () => detectorRef.current.getState(),
  };
}

/**
 * The audio block to record when monitoring was never running.
 *
 * `monitored: false` is not the same as "no sound was made". The engine reads
 * it as "not measured" and falls back to the transcript, rather than scoring
 * Phase 1 B as a failure the child did not earn.
 */
export const UNMONITORED_AUDIO = Object.freeze({
  noiseFloor: null,
  monitored: false,
  vocalisations: [],
  envelope: [],
});

export default useAudioEnergy;
